import { type AthleteSearchFilter, type DuplicateAthleteSearchRequest, type HeatResultFilter } from '@scoring/types';

/**
 * Query key factory.
 *
 * Calling with ids returns the full key for an entity
 * Calling without ids returns the top-level key which can be used to invalidate all entities
 * forAll returns the top-level key
 * forEvent returns the key scoped to an event
 *
 */
export const queryKeys = {
    auth: () => ['AUTH', 'SINGLE'] as const,

    users: {
        list: () => ['USERS', 'LIST'] as const,
        listJudges: () => ['USERS', 'LIST', 'JUDGES'] as const,
        singleAll: () => ['USERS', 'SINGLE'] as const,
        single: (userId: string) => ['USERS', 'SINGLE', userId] as const,
        favouritesAll: () => ['USERS', 'SINGLE', 'FAVOURITES'] as const,
        favourites: (userId: string) => ['USERS', 'SINGLE', 'FAVOURITES', userId] as const,
    },

    athletes: {
        searchAll: () => ['ATHLETES', 'SEARCH'] as const,
        search: (filter?: AthleteSearchFilter) => ['ATHLETES', 'SEARCH', filter ?? null] as const,
        duplicatesAll: () => ['ATHLETES', 'DUPLICATES'] as const,
        duplicates: (request: DuplicateAthleteSearchRequest) => ['ATHLETES', 'DUPLICATES', request] as const,
        eventUsagesAll: () => ['ATHLETES', 'EVENT_USAGES'] as const,
        eventUsages: (globalAthleteIds: string[]) => ['ATHLETES', 'EVENT_USAGES', globalAthleteIds] as const,
        activeEventAll: () => ['ATHLETES', 'LIST', 'ACTIVE_EVENT'] as const,
        activeEvent: (eventId: string) => ['ATHLETES', 'LIST', 'ACTIVE_EVENT', eventId] as const,
    },

    sports: {
        list: () => ['SPORTS', 'LIST'] as const,
    },

    events: {
        list: () => ['EVENTS', 'LIST'] as const,
        searchAll: () => ['EVENTS', 'SEARCH'] as const,
        search: (scope?: string) => ['EVENTS', 'SEARCH', scope ?? 'ALL'] as const,
        activeStructureAll: () => ['EVENTS', 'SINGLE', 'ACTIVE_STRUCTURE'] as const,
        activeStructure: (externalId: string) => ['EVENTS', 'SINGLE', 'ACTIVE_STRUCTURE', externalId] as const,
    },

    series: {
        list: () => ['SERIES', 'LIST'] as const,
        singleAll: () => ['SERIES', 'SINGLE'] as const,
        single: (seriesId: string) => ['SERIES', 'SINGLE', seriesId] as const,
    },

    contributors: {
        activeEventAll: () => ['CONTRIBUTORS', 'LIST', 'ACTIVE_EVENT'] as const,
        activeEvent: (eventId: string) => ['CONTRIBUTORS', 'LIST', 'ACTIVE_EVENT', eventId] as const,
    },

    competitions: {
        judgesAll: () => ['COMPETITIONS', 'SINGLE', 'JUDGES'] as const,
        judges: (eventId: string, competitionId: string) =>
            ['COMPETITIONS', 'SINGLE', 'JUDGES', eventId, competitionId] as const,
    },

    heats: {
        entrantsAll: () => ['HEATS', 'SINGLE', 'ENTRANTS'] as const,
        entrantsForEvent: (eventId: string) => ['HEATS', 'SINGLE', 'ENTRANTS', eventId] as const,
        entrants: (eventId: string, heatId: string) => ['HEATS', 'SINGLE', 'ENTRANTS', eventId, heatId] as const,
        //under the same prefix as the per heat key, so anything that invalidates a heat's entrants invalidates this too
        phaseEntrants: (eventId: string, phaseId: string, heatIds?: string[]) =>
            ['HEATS', 'SINGLE', 'ENTRANTS', eventId, 'PHASE', phaseId, heatIds ?? null] as const,

        judgesAll: () => ['HEATS', 'SINGLE', 'JUDGES'] as const,
        judgesForEvent: (eventId: string) => ['HEATS', 'SINGLE', 'JUDGES', eventId] as const,
        judges: (eventId: string, heatId: string) => ['HEATS', 'SINGLE', 'JUDGES', eventId, heatId] as const,
        //under the same prefix as the per heat key, so anything that invalidates a heat's judges invalidates this too
        phaseJudges: (eventId: string, phaseId: string, heatIds?: string[]) =>
            ['HEATS', 'SINGLE', 'JUDGES', eventId, 'PHASE', phaseId, heatIds ?? null] as const,

        judgeScoreRankingAll: () => ['HEATS', 'SINGLE', 'JUDGE_SCORE_RANKING'] as const,
        judgeScoreRanking: (eventId: string, heatId: string, judgeAssignmentId: string) =>
            ['HEATS', 'SINGLE', 'JUDGE_SCORE_RANKING', eventId, heatId, judgeAssignmentId] as const,

        judgeResultsAll: () => ['HEATS', 'SINGLE', 'JUDGE_RESULTS'] as const,
        judgeResults: (eventId: string, heatId: string, judgeAssignmentId: string) =>
            ['HEATS', 'SINGLE', 'JUDGE_RESULTS', eventId, heatId, judgeAssignmentId] as const,

        resultsAll: () => ['HEATS', 'SINGLE', 'RESULTS'] as const,
        resultsForEvent: (eventId: string) => ['HEATS', 'SINGLE', 'RESULTS', eventId] as const,
        results: (eventId: string, heatId: string, filter?: HeatResultFilter) =>
            ['HEATS', 'SINGLE', 'RESULTS', eventId, heatId, filter ?? null] as const,

        rankingHistoryAll: () => ['HEATS', 'SINGLE', 'RANKING_HISTORY'] as const,
        rankingHistory: (eventId: string, heatId: string) =>
            ['HEATS', 'SINGLE', 'RANKING_HISTORY', eventId, heatId] as const,
    },

    phases: {
        resultsAll: () => ['PHASES', 'SINGLE', 'RESULTS'] as const,
        resultsForEvent: (eventId: string) => ['PHASES', 'SINGLE', 'RESULTS', eventId] as const,
        results: (eventId: string, phaseId: string) => ['PHASES', 'SINGLE', 'RESULTS', eventId, phaseId] as const,

        heatResultsAll: () => ['PHASES', 'SINGLE', 'HEAT_RESULTS'] as const,
        heatResultsForEvent: (eventId: string) => ['PHASES', 'SINGLE', 'HEAT_RESULTS', eventId] as const,
        heatResults: (eventId: string, phaseId: string, filter?: HeatResultFilter) =>
            ['PHASES', 'SINGLE', 'HEAT_RESULTS', eventId, phaseId, filter ?? null] as const,

        judgeResultsAll: () => ['PHASES', 'SINGLE', 'JUDGE_RESULTS'] as const,
        judgeResultsForEvent: (eventId: string) => ['PHASES', 'SINGLE', 'JUDGE_RESULTS', eventId] as const,
        judgeResults: (eventId: string, phaseId: string, judgeId: string) =>
            ['PHASES', 'SINGLE', 'JUDGE_RESULTS', eventId, phaseId, judgeId] as const,

        statistics: (eventId: string, phaseId: string) => ['PHASES', 'SINGLE', 'STATISTICS', eventId, phaseId] as const,
    },

    phaseGroups: {
        resultsAll: () => ['PHASE_GROUPS', 'SINGLE', 'RESULTS'] as const,
        resultsForEvent: (eventId: string) => ['PHASE_GROUPS', 'SINGLE', 'RESULTS', eventId] as const,
        results: (eventId: string, phaseGroupId: string) =>
            ['PHASE_GROUPS', 'SINGLE', 'RESULTS', eventId, phaseGroupId] as const,

        membershipsAll: () => ['PHASE_GROUP_MEMBERSHIPS', 'LIST'] as const,
        membershipsForEvent: (eventId: string) => ['PHASE_GROUP_MEMBERSHIPS', 'LIST', eventId] as const,
        memberships: (eventId: string, phaseGroupId: string) =>
            ['PHASE_GROUP_MEMBERSHIPS', 'LIST', eventId, phaseGroupId] as const,
    },

    categories: {
        resultsAll: () => ['CATEGORIES', 'SINGLE', 'RESULTS'] as const,
        resultsForEvent: (eventId: string) => ['CATEGORIES', 'SINGLE', 'RESULTS', eventId] as const,
        results: (eventId: string, categoryId: string) =>
            ['CATEGORIES', 'SINGLE', 'RESULTS', eventId, categoryId] as const,
    },

    units: {
        entrantsAll: () => ['UNITS', 'SINGLE', 'ENTRANTS'] as const,
        entrantsForEvent: (eventId: string) => ['UNITS', 'SINGLE', 'ENTRANTS', eventId] as const,
        entrants: (eventId: string, unitId: string) => ['UNITS', 'SINGLE', 'ENTRANTS', eventId, unitId] as const,

        rankingAll: () => ['UNITS', 'SINGLE', 'RANKING'] as const,
        ranking: (eventId: string, unitId: string) => ['UNITS', 'SINGLE', 'RANKING', eventId, unitId] as const,

        rankingHistoryAll: () => ['UNITS', 'SINGLE', 'RANKING_HISTORY'] as const,
        rankingHistory: (eventId: string, unitId: string) =>
            ['UNITS', 'SINGLE', 'RANKING_HISTORY', eventId, unitId] as const,

        judgeLiveStatsAll: () => ['UNITS', 'SINGLE', 'JUDGE_LIVE_STATS'] as const,
        judgeLiveStats: (
            eventId: string,
            unitEntrantId: string,
            judgeAssignmentId: string,
            elementGroupId: string | null,
            value: number | null
        ) =>
            [
                'UNITS',
                'SINGLE',
                'JUDGE_LIVE_STATS',
                eventId,
                unitEntrantId,
                judgeAssignmentId,
                elementGroupId,
                value,
            ] as const,
    },

    unitEntrants: {
        statsAll: () => ['UNIT_ENTRANTS', 'SINGLE', 'STATS'] as const,
        statsForEvent: (eventId: string) => ['UNIT_ENTRANTS', 'SINGLE', 'STATS', eventId] as const,
        stats: (eventId: string, unitEntrantId: string) =>
            ['UNIT_ENTRANTS', 'SINGLE', 'STATS', eventId, unitEntrantId] as const,
    },

    judges: {
        assignmentsAll: () => ['JUDGES', 'SINGLE', 'ASSIGNMENTS'] as const,
        assignmentsForEvent: (eventId: string) => ['JUDGES', 'SINGLE', 'ASSIGNMENTS', eventId] as const,
        assignments: (eventId: string, judgeId: string) =>
            ['JUDGES', 'SINGLE', 'ASSIGNMENTS', eventId, judgeId] as const,
        //under the event's assignments prefix, so the streamed assignment updates refresh it too
        myAssignments: (eventId: string) => ['JUDGES', 'SINGLE', 'ASSIGNMENTS', eventId, 'ME'] as const,
        myCategories: () => ['JUDGES', 'ME', 'CATEGORIES'] as const,

        rankingHistoriesAll: () => ['JUDGES', 'SINGLE', 'RANKING_HISTORIES'] as const,
        rankingHistoriesForEvent: (eventId: string) => ['JUDGES', 'SINGLE', 'RANKING_HISTORIES', eventId] as const,
        rankingHistories: (eventId: string, judgeId: string) =>
            ['JUDGES', 'SINGLE', 'RANKING_HISTORIES', eventId, judgeId] as const,
    },

    audits: {
        list: () => ['AUDITS', 'LIST'] as const,
    },

    organisations: {
        list: () => ['ORGANISATIONS', 'LIST'] as const,
    },

    eventResults: {
        manifestAll: () => ['EVENT_RESULTS', 'MANIFEST'] as const,
        manifest: (eventId: string) => ['EVENT_RESULTS', 'MANIFEST', eventId] as const,
    },

    image: {
        singleAll: () => ['IMAGE', 'SINGLE'] as const,
        single: (imageId: string) => ['IMAGE', 'SINGLE', imageId] as const,
    },

    teams: {
        activeEventAll: () => ['TEAMS', 'LIST', 'ACTIVE_EVENT'] as const,
        activeEvent: (eventId: string) => ['TEAMS', 'LIST', 'ACTIVE_EVENT', eventId] as const,
    },

    scoreDimensions: {
        listAll: () => ['SCORE_DIMENSIONS', 'LIST'] as const,
        listForEvent: (eventId: string) => ['SCORE_DIMENSIONS', 'LIST', eventId] as const,
        list: (eventId: string, elementGroupId: string) =>
            ['SCORE_DIMENSIONS', 'LIST', eventId, elementGroupId] as const,
    },

    elements: {
        listAll: () => ['ELEMENTS', 'LIST'] as const,
        listForEvent: (eventId: string) => ['ELEMENTS', 'LIST', eventId] as const,
        list: (eventId: string, unitId: string) => ['ELEMENTS', 'LIST', eventId, unitId] as const,
    },

    scores: {
        listAll: () => ['SCORES', 'LIST'] as const,
        listForEvent: (eventId: string) => ['SCORES', 'LIST', eventId] as const,
        list: (eventId: string, elementId: string) => ['SCORES', 'LIST', eventId, elementId] as const,
    },
} as const;
